import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ListNotificationsParams,
  NotificationNavigation,
  NotificationRecord,
  NotificationStatus
} from 'src/app/notifications/interfaces/notifications-client';
import { AuthService } from 'src/app/core/services/authentication.service';
import {
  BrowserNotificationAvailability,
  BrowserNotificationPermissionState,
  MessagingService
} from 'src/app/notifications/services/messages.services';
import { NotificationsClientService } from 'src/app/notifications/services/notifications-client.service';
import {
  NotificationDetailDialogComponent,
  NotificationDetailDialogData,
  NotificationDetailDialogResult
} from './notification-detail-dialog/notification-detail-dialog.component';
import { NotificationPermissionGuideDialogComponent } from './notification-permission-guide-dialog/notification-permission-guide-dialog.component';
import { SnackBarService } from 'src/app/core/services/snackBar.service';

type NotificationFilter = 'ALL' | NotificationStatus;

interface NotificationScope {
  companyId: string;
  productId: string;
  document: string;
}

@Component({
  selector: 'app-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss']
})
export class UserNotificationsComponent implements OnInit, OnDestroy {
  @Output() closeRequested = new EventEmitter<void>();

  public notifications: NotificationRecord[] = [];
  public activeFilter: NotificationFilter = 'ALL';
  public loading = false;
  public loadingMore = false;
  public actionInProgress = false;
  public errorMessage = '';
  public unreadTotal = 0;
  public page = 1;
  public totalElements = 0;
  public totalPages = 0;
  public nextPage: number = null;
  public previousPage: number = null;
  public hasNextPageValue = false;
  public soundEnabled = true;
  public browserNotificationAvailability: BrowserNotificationAvailability = 'unconfigured';
  public browserNotificationPermission: BrowserNotificationPermissionState = 'unsupported';
  public enablingBrowserNotifications = false;

  private readonly pageSize = 10;
  private subscriptions: Subscription[] = [];
  private deletingIds: { [id: string]: boolean } = {};
  private browserPermissionRefreshListener = () => this.refreshBrowserNotificationPermission(true);
  private notificationPermissionSyncInProgress = false;
  private browserNotificationPermissionStatus: any;
  private currentTime = Date.now();
  private relativeTimerInterval: any = null;

  constructor(
    private authService: AuthService,
    private messagingService: MessagingService,
    private notificationsClient: NotificationsClientService,
    private dialog: MatDialog,
    private router: Router,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    this.refreshBrowserNotificationPermission();
    this.listenBrowserNotificationPermissionChanges();
    this.loadNotifications(false);
    this.notificationsClient.refreshUnreadCount();
    this.subscriptions.push(
      this.messagingService.foregroundMessage$.subscribe(() => this.refreshNotifications()),
      this.notificationsClient.unreadCount$.subscribe(count => this.unreadTotal = count),
      this.notificationsClient.notificationsRefresh$.subscribe(() => this.refreshNotifications()),
      this.messagingService.soundEnabled$.subscribe(enabled => this.soundEnabled = enabled)
    );

    this.ngZone.runOutsideAngular(() => {
      this.relativeTimerInterval = setInterval(() => {
        this.currentTime = Date.now();
        this.cdr.detectChanges();
      }, 30000);
    });
  }

  public toggleSound(): void {
    var nextValue = !this.soundEnabled;
    this.messagingService.setSoundEnabled(nextValue);
    if (nextValue) {
      this.messagingService.unlockNotificationSound();
    }
  }

  ngOnDestroy() {
    if (this.relativeTimerInterval) {
      clearInterval(this.relativeTimerInterval);
      this.relativeTimerInterval = null;
    }
    this.stopBrowserNotificationPermissionChanges();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  public showNotificationAlertsActivation(): boolean {
    return this.browserNotificationAvailability === 'available' &&
      this.browserNotificationPermission !== 'denied' &&
      (this.browserNotificationPermission === 'default' || !this.soundEnabled);
  }

  public showBrowserNotificationBlocked(): boolean {
    return this.browserNotificationAvailability === 'available' &&
      this.browserNotificationPermission === 'denied';
  }

  public showBrowserNotificationUnsupported(): boolean {
    return this.browserNotificationAvailability === 'unsupported';
  }

  public activateNotificationAlerts(): void {
    if (!this.showNotificationAlertsActivation() || this.enablingBrowserNotifications) return;

    this.enablingBrowserNotifications = true;
    this.errorMessage = '';

    this.messagingService.enableNotificationAlerts().then(
      () => {
        this.refreshBrowserNotificationPermission(true);
        this.enablingBrowserNotifications = false;
      },
      () => {
        this.refreshBrowserNotificationPermission(true);
        this.enablingBrowserNotifications = false;
        this.errorMessage = 'No fue posible solicitar el permiso de notificaciones.';
      }
    );
  }

  public openPermissionGuide(): void {
    var ref = this.dialog.open(NotificationPermissionGuideDialogComponent, {
      panelClass: 'notification-permission-guide-panel',
      maxWidth: '94vw',
      width: '520px',
      autoFocus: false,
      restoreFocus: false
    });

    this.subscriptions.push(
      ref.afterClosed().subscribe(() => this.refreshBrowserNotificationPermission(true))
    );
  }

  public setFilter(filter: NotificationFilter): void {
    if (this.activeFilter === filter) return;

    this.activeFilter = filter;
    this.loadNotifications(false);
  }

  public refreshNotifications(): void {
    this.loadNotifications(false);
    this.notificationsClient.refreshUnreadCount();
  }

  public loadMore(): void {
    if (!this.hasNextPage() || this.loadingMore || this.loading) return;

    this.loadNotifications(true);
  }

  public openNotification(notification: NotificationRecord): void {
    if (notification.status === 'UNREAD') {
      this.markNotificationAsRead(notification);
    }

    var data: NotificationDetailDialogData = {
      notification,
      iconClass: this.iconForType(notification.notificationType),
      formattedDate: this.formatDate(notification.createdAt)
    };

    var ref = this.dialog.open<
      NotificationDetailDialogComponent,
      NotificationDetailDialogData,
      NotificationDetailDialogResult
    >(NotificationDetailDialogComponent, {
      data,
      panelClass: 'notification-detail-dialog-panel',
      maxWidth: '92vw',
      width: '480px',
      autoFocus: false,
      restoreFocus: false
    });

    this.subscriptions.push(
      ref.afterClosed().subscribe(result => {
        if (result && result.action === 'delete') {
          this.deleteNotification(result.notification);
        }
        if (result && result.action === 'navigate') {
          this.navigateFromNotification(result.notification);
        }
      })
    );
  }

  public markAllAsRead(): void {
    var scope = this.getNotificationScope();
    if (!scope || this.actionInProgress) return;

    this.actionInProgress = true;
    this.errorMessage = '';
    this.subscriptions.push(
      this.notificationsClient.markAllAsRead(scope).subscribe(
        () => {
          this.actionInProgress = false;
          this.notifications = this.notifications.map(notification => this.asRead(notification));
          this.notificationsClient.clearUnreadCount();
          this.loadNotifications(false);
        },
        (error) => {
          this.actionInProgress = false;
          this.errorMessage = this.getHttpErrorMessage(error);
        }
      )
    );
  }

  public deleteNotification(notification: NotificationRecord, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!notification || this.deletingIds[notification.id]) return;

    this.deletingIds[notification.id] = true;
    this.errorMessage = '';
    var wasUnread = notification.status === 'UNREAD';
    this.subscriptions.push(
      this.notificationsClient.delete(notification.id).subscribe(
        () => {
          delete this.deletingIds[notification.id];
          this.notifications = this.notifications.filter(item => item.id !== notification.id);
          this.totalElements = Math.max(0, this.totalElements - 1);
          if (wasUnread) this.notificationsClient.decrementUnreadCount();
        },
        (error) => {
          delete this.deletingIds[notification.id];
          this.errorMessage = this.getHttpErrorMessage(error);
        }
      )
    );
  }

  public isDeleting(notification: NotificationRecord): boolean {
    return !!(notification && this.deletingIds[notification.id]);
  }

  public hasNextPage(): boolean {
    return this.hasNextPageValue;
  }

  public unreadCount(): number {
    return this.unreadTotal;
  }

  public unreadCountLabel(): string {
    if (this.unreadTotal <= 0) return '';
    return String(this.unreadTotal);
  }

  public formatDate(value: string): string {
    if (!value) return '';

    var date = new Date(value);
    if (isNaN(date.getTime())) return value;

    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  public relativeDate(value: string): string {
    if (!value) return '';

    var date = new Date(value);
    if (isNaN(date.getTime())) return '';

    var diffMs = this.currentTime - date.getTime();
    var diffSec = Math.round(diffMs / 1000);
    var diffMin = Math.round(diffSec / 60);
    var diffHr = Math.round(diffMin / 60);
    var diffDay = Math.round(diffHr / 24);

    if (diffSec < 45) return 'ahora';
    if (diffMin < 60) return 'hace ' + diffMin + ' min';
    if (diffHr < 24) return 'hace ' + diffHr + ' h';
    if (diffDay === 1) return 'ayer';
    if (diffDay < 7) return 'hace ' + diffDay + ' d';

    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short'
    });
  }

  public iconForType(type: string): string {
    if (!type) return 'fa-bell';

    var key = String(type).toUpperCase();

    if (key === 'CARGO') return 'fa-truck';

    return 'fa-bell';
  }

  public trackByNotification(_index: number, notification: NotificationRecord): string {
    return notification.id;
  }

  private refreshBrowserNotificationPermission(syncOnPermissionChange: boolean = false): void {
    var previousPermission = this.browserNotificationPermission;

    this.browserNotificationAvailability = this.messagingService.getBrowserNotificationAvailability();
    this.browserNotificationPermission = this.messagingService.getNotificationPermissionState();

    if (
      syncOnPermissionChange &&
      previousPermission &&
      previousPermission !== this.browserNotificationPermission
    ) {
      this.synchronizeBrowserNotificationPermission();
    }
  }

  private synchronizeBrowserNotificationPermission(): void {
    if (this.notificationPermissionSyncInProgress) return;

    var notificationPermissionEnabled = this.browserNotificationPermission === 'granted';
    this.notificationPermissionSyncInProgress = true;

    if (notificationPermissionEnabled) {
      this.messagingService.registerCurrentUserFcmSubscription(false);
      this.snackBarService.openSnackBar('Notificaciones activadas.', undefined, 'info', 3000);
      this.notificationPermissionSyncInProgress = false;
      return;
    }

    this.messagingService.unregisterCurrentUserFcmSubscription().then(
      () => {
        this.snackBarService.openSnackBar('Notificaciones desactivadas.', undefined, 'info', 3000);
        this.notificationPermissionSyncInProgress = false;
      },
      () => {
        this.snackBarService.openSnackBar(
          'No fue posible actualizar la suscripcion de notificaciones.',
          undefined,
          'error'
        );
        this.notificationPermissionSyncInProgress = false;
      }
    );
  }

  private listenBrowserNotificationPermissionChanges(): void {
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('focus', this.browserPermissionRefreshListener);
    }

    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this.browserPermissionRefreshListener);
    }

    if (typeof navigator !== 'undefined' &&
      (navigator as any).permissions &&
      typeof (navigator as any).permissions.query === 'function') {
      (navigator as any).permissions.query({ name: 'notifications' }).then(
        (permissionStatus) => {
          this.browserNotificationPermissionStatus = permissionStatus;
          this.browserNotificationPermissionStatus.onchange = this.browserPermissionRefreshListener;
        },
        () => {
          this.browserNotificationPermissionStatus = null;
        }
      );
    }
  }

  private stopBrowserNotificationPermissionChanges(): void {
    if (typeof window !== 'undefined' && window.removeEventListener) {
      window.removeEventListener('focus', this.browserPermissionRefreshListener);
    }

    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener('visibilitychange', this.browserPermissionRefreshListener);
    }

    if (this.browserNotificationPermissionStatus) {
      this.browserNotificationPermissionStatus.onchange = null;
      this.browserNotificationPermissionStatus = null;
    }
  }

  private loadNotifications(append: boolean): void {
    var scope = this.getNotificationScope();
    if (!scope) return;

    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
      this.notifications = [];
      this.page = 1;
      this.nextPage = null;
      this.previousPage = null;
      this.hasNextPageValue = false;
      this.totalElements = 0;
      this.totalPages = 0;
    }

    this.errorMessage = '';

    var params: ListNotificationsParams = {
      companyId: scope.companyId,
      productId: scope.productId,
      document: scope.document,
      page: append && this.nextPage ? this.nextPage : 1,
      pageSize: this.pageSize
    };
    var cursor = append ? this.getNextPageCursor() : null;

    if (this.activeFilter !== 'ALL') {
      params.status = this.activeFilter;
    }

    if (cursor) {
      params.cursorCreatedAt = cursor.createdAt;
      params.cursorId = cursor.id;
    }

    this.subscriptions.push(
      this.notificationsClient.listByUser(params).subscribe(
        (response) => {
          this.loading = false;
          this.loadingMore = false;
          this.page = response.page;
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.nextPage = response.nextPage;
          this.previousPage = response.previousPage;
          this.hasNextPageValue = response.hasNextPage;
          this.notifications = append
            ? this.notifications.concat(response.items || [])
            : response.items || [];
        },
        (error) => {
          this.loading = false;
          this.loadingMore = false;
          this.errorMessage = this.getHttpErrorMessage(error);
          if (!append) {
            this.notifications = [];
          }
        }
      )
    );
  }

  private getNextPageCursor(): { createdAt: string; id: string } | null {
    if (!this.notifications || this.notifications.length === 0) return null;

    var lastNotification = this.notifications[this.notifications.length - 1];
    if (!lastNotification || !lastNotification.createdAt || !lastNotification.id) return null;

    return {
      createdAt: lastNotification.createdAt,
      id: lastNotification.id
    };
  }

  private markNotificationAsRead(notification: NotificationRecord): void {
    var previous = Object.assign({}, notification);
    Object.assign(notification, this.asRead(notification));

    this.notificationsClient.decrementUnreadCount();
    this.subscriptions.push(
      this.notificationsClient.markAsRead(notification.id).subscribe(
        (updatedNotification) => {
          Object.assign(notification, updatedNotification);
          if (this.activeFilter === 'UNREAD') {
            this.notifications = this.notifications.filter(item => item.id !== updatedNotification.id);
          }
        },
        (error) => {
          Object.assign(notification, previous);
          this.errorMessage = this.getHttpErrorMessage(error);
          this.notificationsClient.refreshUnreadCount();
        }
      )
    );
  }

  private navigateFromNotification(notification: NotificationRecord): void {
    var route = this.resolveNavigationRoute(notification ? notification.navigation : null);
    if (!route) return;

    this.closeRequested.emit();
    this.navigateReloadingRoute(route);
  }

  private resolveNavigationRoute(navigation: NotificationNavigation | null): any[] | null {
    if (!navigation || !navigation.entityId) return null;

    if (navigation.type === 'CARGO_DETAIL') {
      return ['/cargo/detail', navigation.entityId];
    }

    return null;
  }

  private navigateReloadingRoute(route: any[]): void {
    var originalShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute;
    var routerConfig = this.router as any;
    var originalOnSameUrlNavigation = routerConfig.onSameUrlNavigation;

    this.router.routeReuseStrategy.shouldReuseRoute = function () { return false; };
    routerConfig.onSameUrlNavigation = 'reload';

    this.router.navigate(route).then(
      () => this.restoreRouteReuse(originalShouldReuseRoute, originalOnSameUrlNavigation),
      () => this.restoreRouteReuse(originalShouldReuseRoute, originalOnSameUrlNavigation)
    );
  }

  private restoreRouteReuse(originalShouldReuseRoute: any, originalOnSameUrlNavigation: any): void {
    setTimeout(() => {
      this.router.routeReuseStrategy.shouldReuseRoute = originalShouldReuseRoute;
      (this.router as any).onSameUrlNavigation = originalOnSameUrlNavigation;
    });
  }

  private asRead(notification: NotificationRecord): NotificationRecord {
    var readAt = notification.readAt || new Date().toISOString();

    return Object.assign({}, notification, {
      status: 'READ',
      readAt,
      updatedAt: readAt
    });
  }

  private getNotificationScope(): NotificationScope | null {
    var settings = (environment as any).notifications;
    if (!settings || settings.enabled === false || !settings.serverUrl || !settings.productId) {
      console.warn('[Notifications] notifications.serverUrl or notifications.productId is not configured; skipping inbox load');
      this.errorMessage = 'Notificaciones no disponibles en este ambiente.';
      this.loading = false;
      this.loadingMore = false;
      return null;
    }

    if (!this.authService || !this.authService.sessionActive()) {
      this.errorMessage = 'Sesion no activa.';
      return null;
    }

    var user = this.authService.getUserSession();
    var document = user && user.information ? user.information.document : null;
    var companyId = user ? user.clientCompanyId : null;

    if (!document || !companyId) {
      this.errorMessage = 'No fue posible identificar el usuario.';
      return null;
    }

    return {
      companyId,
      productId: settings.productId,
      document
    };
  }

  private getHttpErrorMessage(error: any): string {
    var status = error && typeof error.status === 'number' ? error.status : null;
    var serverMessage = error && error.error && error.error.message ? String(error.error.message) : '';

    if (status === 0) {
      return 'Sin conexión. Revisa tu red e inténtalo de nuevo.';
    }
    if (status === 401 || status === 403) {
      return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    }
    if (status === 404) {
      return 'No encontramos esta notificación.';
    }
    if (status === 408 || status === 504) {
      return 'El servidor tardó demasiado en responder. Inténtalo de nuevo.';
    }
    if (status === 429) {
      return 'Demasiados intentos. Espera unos segundos e inténtalo de nuevo.';
    }
    if (status === 503) {
      return 'El servicio de notificaciones no está disponible en este momento.';
    }
    if (status >= 500) {
      return 'El servicio de notificaciones falló. Inténtalo de nuevo en unos minutos.';
    }
    if (status >= 400 && serverMessage && !/^Http failure/i.test(serverMessage)) {
      return serverMessage;
    }

    return 'No fue posible cargar las notificaciones. Inténtalo de nuevo.';
  }
}
